import { Dispatch, useState } from 'react';
import {
  Card,
  CardHeader,
  Disclosure,
  DisclosureContent,
  DisclosureTrigger,
  Field,
  Flex,
  Input,
  Skeleton,
  Text,
} from '@optiaxiom/react';

import { InferenceTypeSelector } from '@frontend/components/InferenceTypeSelector';
import { TiptapEditorWrapper } from '@frontend/components/TiptapEditor';
import type { SpecializedAgentAction } from '@frontend/features/agents/hooks';
import { useOpalUser } from '@frontend/hooks';
import * as agentStyles from '@frontend/styles/form.css';
import { SpecializedAgent } from '@frontend/types';
import { Tool } from '@frontend/types/tools';

import { AgentFormFiles } from './AgentFormFiles';
import { styles } from './AgentFormInput.css';
import { ToolsInput } from './ToolsInput';
import { VariableInput } from './VariableInput';

type AgentFormInputProps = {
  formData: SpecializedAgent;
  dispatch: Dispatch<SpecializedAgentAction>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shouldLoadPromptTemplate: boolean;
  shouldShowUnusedParamsWarning: boolean;
  lockedFields?: string[];
  selectedTool: Tool | null;
  onToolSelect: (tool: Tool | null) => void;
};

export function AgentFormInput({
  formData,
  dispatch,
  open,
  onOpenChange,
  shouldLoadPromptTemplate,
  shouldShowUnusedParamsWarning,
  lockedFields = [],
  selectedTool,
  onToolSelect,
}: AgentFormInputProps) {
  const parameters = formData.parameters || [];
  const agentToolNames: string[] = formData.enabled_tools || [];

  const { opalUser } = useOpalUser();
  const [promptError, setPromptError] = useState<string | null>(null);

  return (
    <Flex className={styles.container}>
      <Card bg="bg.secondary" px="8" py="4">
        <Disclosure w="full" onOpenChange={onOpenChange} open={open}>
          <CardHeader>
            <DisclosureTrigger>
              <Text className={styles.fieldTitle}>Input</Text>
            </DisclosureTrigger>
          </CardHeader>
          <DisclosureContent>
            <Flex pt="24" pb="20" px="24">
              <Field
                description="Place dynamic variables in double brackets, eg. [[variable_name]]."
                label="Prompt Template"
                info="Defines the prompt and core logic of this agent. This can include objectives, tasks, and output."
                error={promptError}
                required
                className={agentStyles.FormInputFieldMaxWidth}
              >
                {formData.prompt_template === undefined && shouldLoadPromptTemplate ? (
                  <Skeleton h="56" />
                ) : (
                  <TiptapEditorWrapper
                    placeholder="Enter your prompt template"
                    className={styles.promptTemplate}
                    value={formData.prompt_template || ''}
                    selectables={formData.parameters || []}
                    usedIn="agent-form"
                    onChange={(value: string) => {
                      dispatch({
                        type: 'UPDATE_FIELD',
                        field: 'prompt_template',
                        value,
                      });
                      if (!value || value.trim() === '') {
                        setPromptError('Prompt Template is required.');
                      } else {
                        setPromptError(null);
                      }
                    }}
                    disabled={lockedFields.includes('prompt_template')}
                  />
                )}
              </Field>
              <VariableInput
                parameters={parameters}
                dispatch={dispatch}
                promptTemplate={formData.prompt_template}
                shouldShowUnusedParamsWarning={shouldShowUnusedParamsWarning}
                disabled={lockedFields.includes('variables')}
              />
              <ToolsInput
                agentToolNames={agentToolNames}
                dispatch={dispatch}
                opalInstanceId={opalUser.instance.instance_id}
                disabled={lockedFields.includes('tools')}
                selectedTool={selectedTool}
                onToolSelect={onToolSelect}
              />
              <Field
                label="Inference Level"
                info="Controls how much reasoning Opal applies when generating its response. Higher inference levels can take more time, but apply deeper reasoning to output"
              >
                <InferenceTypeSelector
                  value={formData.inference_type}
                  onChange={inference_type =>
                    dispatch({
                      type: 'UPDATE_FIELD',
                      field: 'inference_type',
                      value: inference_type,
                    })
                  }
                  disabled={lockedFields.includes('inference')}
                />
              </Field>
              <Field
                label="Creativity"
                info="(0-1 scale) controls the randomness of Opal’s output and how creative it should be. Lower values produce more predictable results, while higher values permit more variety."
              >
                <Flex flexDirection="row" gap="8">
                  <Input
                    placeholder="0"
                    value={formData.creativity?.toString() || '0'}
                    onChange={e => {
                      const value = parseFloat(e.target.value) || 0;
                      const clampedValue = Math.max(0, Math.min(1, value));
                      dispatch({ type: 'UPDATE_FIELD', field: 'creativity', value: clampedValue });
                    }}
                    type="number"
                    min="0"
                    max="1"
                    step="0.1"
                    disabled={lockedFields.includes('creativity')}
                  />
                </Flex>
              </Field>
              <Field label="Files" info="Attach reference files for your agent here">
                <AgentFormFiles
                  formData={formData}
                  dispatch={dispatch}
                  disabled={lockedFields.includes('files')}
                />
              </Field>
            </Flex>
          </DisclosureContent>
        </Disclosure>
      </Card>
    </Flex>
  );
}
